import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Table,
  Button,
} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import AdLogsService from "../../../services/OrderingServices/AdLogsService";

const AdLogsDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [adLog, setAdLog] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (id) {
      fetchAdLogDetail();
    }
  }, [id]);

  async function fetchAdLogDetail() {
    try {
      setLoading(true);
      const response = await AdLogsService.getAdLogById(id);
      if (response) {
        // Handle both direct object and wrapped response
        const logData = response.data || response;
        setAdLog(logData);
      }
    } catch (error) {
      toast.error("Failed to fetch ad log details");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  const getDisplayReasonText = (reason) => {
    const reasons = {
      0: "Unknown",
      1: "Scheduled",
      2: "Rotation",
      3: "Event Trigger",
      4: "Manual",
    };
    return reasons[reason] || "Unknown";
  };

  const getStatusBadge = (value, trueColor = "success", falseColor = "danger") => {
    return (
      <Badge color={value ? trueColor : falseColor}>
        {value ? "Yes" : "No"}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", marginTop: "50px" }}>
        <Triangle color="#435ebe" height={80} width={80} />
      </div>
    );
  }

  if (!adLog) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Ad Log Details" breadcrumbItem="Details" />
          <Row>
            <Col lg="12">
              <Card>
                <CardBody className="text-center">
                  <p>Ad log not found</p>
                  <Button color="primary" onClick={() => navigate("/ads/logs")}>
                    Back to Logs
                  </Button>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Ad Log Details"
            breadcrumbItem="View Log"
          />

          <Row>
            <Col lg="12">
              <Card>
                <CardHeader className="bg-light">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                      Ad Log Details - Log ID #{adLog.id} | Ad #{adLog.adId}
                    </h5>
                    <span className="text-muted small">
                      Created: {adLog.createdAt ? new Date(adLog.createdAt).toLocaleString() : "-"}
                    </span>
                  </div>
                </CardHeader>
                <CardBody>
                  <Row className="mb-4">
                    <Col md="6">
                      <h6 className="text-muted mb-3">Device Information</h6>
                      <Table borderless>
                        <tbody>
                          <tr>
                            <td className="fw-bold">Device Name:</td>
                            <td>{adLog.deviceName || "-"}</td>
                          </tr>
                          <tr>
                            <td className="fw-bold">MAC ID:</td>
                            <td>{adLog.macId || "-"}</td>
                          </tr>
                          <tr>
                            <td className="fw-bold">IP Address:</td>
                            <td>{adLog.ipAddress || "-"}</td>
                          </tr>
                          <tr>
                            <td className="fw-bold">Screen Name:</td>
                            <td>{adLog.screenName || "-"}</td>
                          </tr>
                          <tr>
                            <td className="fw-bold">App Version:</td>
                            <td>{adLog.appVersion || "-"}</td>
                          </tr>
                        </tbody>
                      </Table>
                    </Col>
                    <Col md="6">
                      <h6 className="text-muted mb-3">Timeline Information</h6>
                      <Table borderless>
                        <tbody>
                          <tr>
                            <td className="fw-bold">Shown At:</td>
                            <td>
                              {adLog.shownAt
                                ? new Date(adLog.shownAt).toLocaleString()
                                : "-"}
                            </td>
                          </tr>
                          <tr>
                            <td className="fw-bold">Hidden At:</td>
                            <td>
                              {adLog.hiddenAt
                                ? new Date(adLog.hiddenAt).toLocaleString()
                                : "-"}
                            </td>
                          </tr>
                          <tr>
                            <td className="fw-bold">Visible Duration:</td>
                            <td>{adLog.visibleSeconds || 0} seconds</td>
                          </tr>
                          <tr>
                            <td className="fw-bold">Played Duration:</td>
                            <td>{adLog.playedSeconds || 0} seconds</td>
                          </tr>
                          <tr>
                            <td className="fw-bold">Display Reason:</td>
                            <td>{getDisplayReasonText(adLog.displayReason)}</td>
                          </tr>
                        </tbody>
                      </Table>
                    </Col>
                  </Row>

                  <Row className="mb-4">
                    <Col md="6">
                      <h6 className="text-muted mb-3">Status</h6>
                      <Table borderless>
                        <tbody>
                          <tr>
                            <td className="fw-bold">Log ID:</td>
                            <td>#{adLog.id}</td>
                          </tr>
                          <tr>
                            <td className="fw-bold">Was Completed:</td>
                            <td>
                              {getStatusBadge(
                                adLog.wasCompleted,
                                "success",
                                "danger"
                              )}
                            </td>
                          </tr>
                          <tr>
                            <td className="fw-bold">Was Skipped:</td>
                            <td>
                              {getStatusBadge(
                                adLog.wasSkipped,
                                "warning",
                                "info"
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </Col>
                    <Col md="6">
                      <h6 className="text-muted mb-3">Log Metadata</h6>
                      <Table borderless>
                        <tbody>
                          <tr>
                            <td className="fw-bold">Created At:</td>
                            <td>
                              {adLog.createdAt
                                ? new Date(adLog.createdAt).toLocaleString()
                                : "-"}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </Col>
                  </Row>

                  <Row className="mt-4">
                    <Col>
                      <Button
                        color="secondary"
                        onClick={() => navigate("/ads/logs")}
                      >
                        <i className="bx bx-arrow-back me-2"></i>Back to Logs
                      </Button>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default AdLogsDetail;
